import { Button } from '@nextui-org/react'
import {
    BubbleMenu,
    BubbleMenuProps,
    getSchema,
    isNodeSelection,
} from '@tiptap/react'
import { useCompletion } from 'ai/react'
import {
    BoldIcon,
    Check,
    CodeIcon,
    ItalicIcon,
    LinkIcon,
    StrikethroughIcon,
    Trash,
    UnderlineIcon,
} from 'lucide-react'
import { Slice } from 'prosemirror-model'
import { Selection } from 'prosemirror-state'
import {
    Dispatch,
    FC,
    SetStateAction,
    forwardRef,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import { toast } from 'react-hot-toast'

import { useStore } from '@nanostores/react'
import { TextSelection } from 'prosemirror-state'
import React from 'react'
import { createExtensions } from 'website/src/extensions'
import { atoms } from 'website/src/lib/atoms'
import { blockTypes, markTypes } from 'website/src/lib/editor-types'
import {
    cn,
    isStreamedDocReady,
    schemaGetter,
} from 'website/src/lib/editor-utils'
import {
    useRootProps,
    useUpdateOnEditorTransaction,
} from 'website/src/lib/hooks'
import { toTipTap } from 'website/src/lib/markdown-to-prosemirror'
import { notifyError } from '../../../lib/sentry'

export interface BubbleMenuItem {
    name: string
    isActive: () => boolean
    command: () => void
    icon: typeof BoldIcon
}

type EditorBubbleMenuProps = Omit<BubbleMenuProps, 'children'>

export const EditorBubbleMenu: FC<EditorBubbleMenuProps> = (props) => {
    const editor = useStore(atoms.editor)!
    useUpdateOnEditorTransaction(editor)
    const items: BubbleMenuItem[] = [
        {
            name: 'bold',
            isActive: () => editor?.isActive('bold'),
            command: () => editor.chain().focus().toggleBold().run(),
            icon: BoldIcon,
        },
        {
            name: 'italic',
            isActive: () => editor?.isActive('italic'),
            command: () => editor.chain().focus().toggleItalic().run(),
            icon: ItalicIcon,
        },
        {
            name: 'underline',
            isActive: () => editor?.isActive('underline'),
            command: () => editor.chain().focus().toggleUnderline().run(),
            icon: UnderlineIcon,
        },
        {
            name: 'strike',
            isActive: () => editor?.isActive('strike'),
            command: () => editor.chain().focus().toggleStrike().run(),
            icon: StrikethroughIcon,
        },
        {
            name: 'code',
            isActive: () => editor?.isActive('code'),
            command: () => editor.chain().focus().toggleCode().run(),
            icon: CodeIcon,
        },
    ]
    let hide = useRef(() => {})
    const hideOnEsc = {
        name: 'hideOnEsc',
        defaultValue: true,
        fn(instance) {
            hide.current = () => instance.hide()
            function onKeyDown(e) {
                if (e.keyCode === 27) {
                    instance.hide()
                    editor?.view?.focus()
                }
            }

            return {
                onShow() {
                    document.addEventListener('keydown', onKeyDown)
                },
                onHide() {
                    document.removeEventListener('keydown', onKeyDown)
                },
            }
        },
    }
    const [formattable, setFormattable] = useState(false)
    const aiInputRef = useRef<HTMLInputElement>(null)

    const bubbleMenuProps: EditorBubbleMenuProps = {
        ...props,

        shouldShow: ({ editor, state, view }) => {
            // don't show if image is selected
            if (editor?.isActive('image') || editor?.isActive('table')) {
                return false
            }
            if (atoms.currentLink.get()?.current) {
                return false
            }

            const { selection } = state
            const { empty } = selection
            const hasEditorFocus = view.hasFocus()

            // don't show if image is selected
            if (
                editor?.isActive('image') ||
                !hasEditorFocus ||
                empty ||
                isNodeSelection(selection)
            ) {
                return false
            }
            return true

            return editor.view.state.selection.content().size > 0
        },
        tippyOptions: {
            moveTransition: 'transform 0.15s ease-out',
            interactive: true,
            placement: 'bottom',
            onHidden: () => {
                setIsAiOpen(false)
                setIsLinkSelectorOpen(false)
                atoms.currentLink.set({ current: null })
            },
            onShow(instance) {
                if (
                    editor?.isActive(blockTypes.codeBlock) ||
                    editor?.isActive(blockTypes.mdxCode) ||
                    editor?.isActive(blockTypes.htmlCode) ||
                    editor?.isActive(blockTypes.text) ||
                    editor?.isActive(markTypes.dummyMdxExpression) ||
                    editor?.isActive(markTypes.mdxExpression)
                ) {
                    setFormattable(false)
                } else {
                    setFormattable(true)
                }
                function onKeyDown(e) {
                    if (!instance.state.isVisible) {
                        return
                    }

                    if (e.key === 'Tab') {
                        console.log('focusing first')
                        if (aiInputRef.current) {
                            aiInputRef.current?.focus()
                            aiInputRef.current?.select()
                        } else {
                            firstButton.current?.focus()
                        }
                        e.preventDefault()
                        e.stopPropagation()
                        document.removeEventListener('keydown', onKeyDown)
                    }
                }
                document.addEventListener('keydown', onKeyDown)
            },
            plugins: [hideOnEsc],
        },
    }

    const [isAiSelectorOpen, setIsAiOpen] = useState(false)

    const currentLink = useStore(atoms.currentLink)
    let [isLinkSelectorOpen, setIsLinkSelectorOpen] = useState(false)

    const firstButton = React.useRef<HTMLButtonElement>()

    return (
        <BubbleMenu
            {...bubbleMenuProps}
            className='relative px-[4px] gap-1 shadow-lg py-1 not-prose bg-black flex w-fit text-white rounded-lg'
        >
            {/* <NodeSelector
                editor={props.editor}
                isOpen={isNodeSelectorOpen}
                setIsOpen={() => {
                    setIsNodeSelectorOpen(!isNodeSelectorOpen)
                    setIsColorSelectorOpen(false)
                    setIsLinkSelectorOpen(false)
                }}
            /> */}

            <AIEdit
                editor={props.editor}
                ref={firstButton}
                inputRef={aiInputRef}
                isOpen={isAiSelectorOpen}
                setIsOpen={() => {
                    setIsAiOpen(!isAiSelectorOpen)
                    setIsLinkSelectorOpen(false)
                }}
            />
            {formattable && (
                <>
                    <LinkSelector
                        editor={props.editor}
                        isOpen={isLinkSelectorOpen || !!currentLink?.href}
                        setIsOpen={() => {
                            setIsLinkSelectorOpen(!isLinkSelectorOpen)
                            setIsAiOpen(false)
                        }}
                    />

                    {items.map((item, index) => {
                        const active = item.isActive()
                        return (
                            <button
                                key={index}
                                onClick={item.command}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.stopPropagation()
                                        e.preventDefault()
                                        item.command()
                                        // console.log(e.target)
                                        setTimeout(() => {
                                            // @ts-ignore
                                            e.target.focus()
                                        }, 10)
                                        // e.target.blur()
                                    }
                                }}
                                className='px-3 py-0 ring-gray-700 ring-inset focus:!outline-none rounded-md focus:bg-gray-900 hover:bg-gray-900'
                            >
                                <item.icon
                                    strokeWidth={active ? 4 : 2}
                                    className={cn(
                                        'w-[16px] !m-0 ',
                                        active && 'text-blue-200',
                                    )}
                                />
                            </button>
                        )
                    })}
                </>
            )}
        </BubbleMenu>
    )
}

interface LinkSelectorProps {
    editor: BubbleMenuProps['editor']
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    ref?: any
}

export const LinkSelector = forwardRef<any, LinkSelectorProps>(
    ({ isOpen, setIsOpen }, ref) => {
        const editor = useStore(atoms.editor)!
        const inputRef = useRef<HTMLInputElement>(null)

        // Autofocus on input by default
        useEffect(() => {
            inputRef.current && inputRef.current?.focus()
        })
        const active = editor?.isActive('link')

        return (
            <>
                <button
                    ref={ref}
                    className='flex h-full ring-gray-700 ring-inset focus:!outline-none rounded-md items-center space-x-2 px-3 py-1 text-sm font-medium text-white hover:bg-gray-900 focus-visible:bg-gray-900'
                    onClick={() => {
                        setIsOpen(!isOpen)
                    }}
                >
                    <LinkIcon
                        strokeWidth={active ? 4 : 2}
                        className={cn(
                            'w-[16px] !m-0 ',
                            active && 'text-blue-200',
                        )}
                    />
                </button>
                {isOpen && (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault()
                            const input = e.target[0] as HTMLInputElement
                            editor
                                .chain()
                                .focus()
                                .setLink({ href: input.value })
                                .run()
                            setIsOpen(false)
                            setTimeout(() => {
                                // @ts-ignore
                                ref?.current?.focus?.()
                            }, 0)
                        }}
                        className='fixed rounded-lg inset-0 z-[99999] flex  overflow-hidden  bg-black p-1 shadow-xl  slide-in-from-top-1'
                    >
                        <input
                            ref={inputRef}
                            // type='url'
                            placeholder='Paste a link'
                            // TODO link validation
                            className='flex-1 bg-black p-1 text-sm outline-none'
                            defaultValue={
                                editor.getAttributes('link').href || ''
                            }
                        />
                        {editor.getAttributes('link').href ? (
                            <button
                                className='flex items-center rounded-sm font-bold p-1 hover:bg-gray-100 dark:hover:bg-gray-900 text-red-400 transition-all '
                                onClick={() => {
                                    editor.chain().focus().unsetLink().run()
                                    setIsOpen(false)
                                }}
                                type='button'
                            >
                                <Trash className='h-4 w-4' />
                            </button>
                        ) : (
                            <button className='flex items-center rounded-sm p-1 text-stone-600 transition-all hover:bg-stone-100'>
                                <Check className='h-4 w-4' />
                            </button>
                        )}
                    </form>
                )}
            </>
        )
    },
)

export const AIEdit = forwardRef<any, any>(
    ({ isOpen, inputRef, setIsOpen }, ref) => {
        const editor = useStore(atoms.editor)!

        const selection = useRef<Selection>()
        const { pageSlug, userId } = useRootProps()
        const contentBeingReplaced = useRef<Slice>()
        // Autofocus on input by default
        useEffect(() => {
            if (!isOpen) {
                return
            }
            const sel = editor.view.state.selection
            if (sel.from !== sel.to) {
                selection.current = sel
            }

            inputRef.current && inputRef.current?.focus()
        }, [isOpen])

        const { complete, completion, isLoading, stop } = useCompletion({
            id: 'holocron',
            api: '/api/edit-selection',
            onFinish: (_prompt, completion) => {
                // console.log('completion', completion)
                // editor?.commands.setTextSelection({
                //     from: selection.current!.from - completion.length,
                //     to: selection.current!.from,
                // })
            },
            onError: (err) => {
                toast.error(err.message)
                if (
                    err.message ===
                    'You have reached your request limit for the day.'
                ) {
                }
            },
        })

        // Insert chunks of the generated text
        useEffect(() => {
            const sel = selection.current
            if (!sel) {
                // console.log('no selection')
                return
            }
            if (!completion) {
                // console.log('no selection')
                return
            }
            // console.log('selection', sel.from, sel.to)
            const tipTapNode = (() => {
                try {
                    // console.log('completion\n', completion)
                    let { node, error } = toTipTap({ str: completion, pageSlug })
                    if (error) {
                        notifyError(error)
                        return
                    }
                    const { ready } = isStreamedDocReady(node!)
                    if (!ready) {
                        return
                    }
                    console.log(`node:\n`, node)
                    return node
                } catch (e: any) {
                    console.log(e.message)
                    console.log(`partial markdown not valid\n`)
                }
            })()
            if (!tipTapNode) {
                return
            }
            const slice = new Slice(
                schemaGetter(pageSlug).nodeFromJSON({
                    type: 'doc',
                    content: tipTapNode.content,
                }).content,
                0,
                0,
            )
            console.log('doc', editor.state.doc.content.size)

            try {
                editor
                    .chain()
                    .deleteRange({
                        from: sel.from,
                        to: sel.to,
                    })
                    .insertContentAt(sel.from, tipTapNode.content!)
                    // .setTextSelection({
                    //     from: sel.from,
                    //     to: sel.from + slice.content.size - 1,
                    // })
                    .run()

                selection.current = TextSelection.create(
                    editor.state.doc,
                    sel.from,
                    sel.from + slice.content.size,
                )
            } catch (e: any) {
                console.log(e.message)
            }
            // selection.current = {
            //     from: sel.from,
            //     to: sel.from + slice.content.size - 1,
            // }

            // TODO how to incrementally parse the markdown?
        }, [completion])

        useEffect(() => {
            // if user presses escape or cmd + z and it's loading,
            // stop the request, delete the completion, and insert back the "++"
            const onKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Escape' || (e.metaKey && e.key === 'z')) {
                    stop()
                    if (e.key === 'Escape') {
                        const old = contentBeingReplaced?.current?.content || []
                        console.log(
                            `stopping completion, replacing with old:`,
                            old,
                        )
                        editor
                            ?.chain()
                            .deleteRange(selection.current!)
                            .insertContentAt(selection.current!.from, old)
                            .run()
                    }
                    // editor?.commands.insertContent('++')
                }
            }
            const mousedownHandler = (e: MouseEvent) => {
                e.preventDefault()
                e.stopPropagation()
                // stop()
                // if (window.confirm('AI writing paused. Continue?')) {
                //     complete(editor?.getText() || '')
                // }
            }
            if (isLoading) {
                document.addEventListener('keydown', onKeyDown)
                window.addEventListener('mousedown', mousedownHandler)
            } else {
                document.removeEventListener('keydown', onKeyDown)
                window.removeEventListener('mousedown', mousedownHandler)
            }
            return () => {
                document.removeEventListener('keydown', onKeyDown)
                window.removeEventListener('mousedown', mousedownHandler)
            }
        }, [stop, isLoading, editor, complete, completion.length])

        function adjustHeight() {
            if (!inputRef.current) {
                return
            }
            inputRef.current.style.height = 'inherit'
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`
        }

        useLayoutEffect(adjustHeight, [])

        return (
            <>
                <button
                    ref={ref}
                    className='flex min-h-full truncate ring-gray-700 ring-inset focus:!outline-none rounded-md items-center space-x-2 px-3 py-1 text-sm font-medium text-white hover:bg-gray-900 focus-visible:bg-gray-900'
                    onClick={() => {
                        setIsOpen(!isOpen)
                    }}
                >
                    <p
                        className={cn(
                            'decoration-stone-400 text-sm underline-offset-4',
                        )}
                    >
                        Edit With AI
                    </p>
                </button>
                {isOpen && (
                    <form
                        onSubmit={async (e) => {
                            e.preventDefault()
                            if (!userId) {
                                return atoms.showSignupModal.set(true)
                            }

                            const input = e.target[0] as HTMLInputElement
                            const asking = input.value
                            if (!selection.current) {
                                return
                            }

                            // editor.commands.setTextSelection(selection.current)

                            // TODO get parent until the node can stay as child of doc

                            // TODO i cannot edit only a part of a node, i need to expand the selection to the whole paragraph or node selected. otherwise markdown will return a whole new paragraph block instead of text
                            const from = selection.current.$from.start() - 1
                            const to = selection.current.$to.end()
                            editor.view.focus()
                            editor.commands.setTextSelection({
                                from,
                                to,
                            })

                            const sel = TextSelection.create(
                                editor.state.doc,
                                from,
                                to,
                            )
                            selection.current = sel
                            const json = sel.content().toJSON()
                            contentBeingReplaced.current = sel
                                .content()
                                .toJSON()

                            console.log(
                                'selection content',
                                JSON.stringify(json.content, null, 2),
                            )
                            const schema = schemaGetter(pageSlug)
                            const node = schema.nodeFromJSON({
                                type: 'doc',
                                content: json.content,
                            })

                            let md: string =
                                editor.storage.markdown.serializer.serialize(
                                    node,
                                )

                            console.log('md\n', md, '\nmd end')

                            complete('', {
                                body: {
                                    asking,
                                    content: md,
                                },
                            })
                        }}
                        className='fixed justify-start rounded-lg top-0 left-0 right-0 max-h-[1000px] min-w-[300px] z-[99999] flex flex-col overflow-visible bg-black p-1 shadow-xl animate-in fade-in slide-in-from-top-1'
                    >
                        <textarea
                            ref={inputRef}
                            // type='url'
                            onChange={() => {
                                adjustHeight()
                            }}
                            placeholder='Make it shorter'
                            // TODO link validation
                            className='grow bg-black p-1 text-sm outline-none'
                            defaultValue={''}
                        />
                        <div className='flex grow w-full p-1  '>
                            <div className='grow'></div>
                            <Button type='submit' size='sm'>
                                Replace
                            </Button>
                        </div>
                    </form>
                )}
            </>
        )
    },
)
